import { ArrowRight } from "lucide-react";
import { cn } from "../../lib/utils";
import { Badge } from "./badge";
import { Button } from "./button";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

export function FileItemTitle({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      {...props}
      className={cn("mb-1 text-sm inline-flex gap-1 items-center", className)}
    />
  );
}

export function FileItemDetails({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      {...props}
      className={cn("text-muted-foreground text-xs", className)}
    />
  );
}

export function FileItemTypeIcon({
  className,
  children,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      {...props}
      className={cn(
        "relative [&_svg]:size-7 [&_svg]:text-muted-foreground",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function FileItemInfos({
  className,
  children,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div {...props} className={cn("flex-1 relative flex", className)}>
      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out -inset-1.5 rounded-lg bg-accent group z-0 absolute flex justify-end items-center px-2">
        <ArrowRight className="text-accent-foreground/10" />
      </div>
      <div className="size-full z-10 flex flex-col ">{children}</div>
    </div>
  );
}

export function FileItemActions({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div {...props} className={cn("flex items-center gap-1", className)} />
  );
}

export function FileItemAction({
  className,
  tooltip,
  ...props
}: React.ComponentProps<typeof Button> & { tooltip: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button size="icon" variant="ghost" {...props} />
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

export function FileItemTypeBadge({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <Badge
      size="xs"
      variant="secondary"
      {...props}
      className={cn("absolute text-[9px] -bottom-1 left-2", className)}
    />
  );
}

export function FileItem({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      {...props}
      className={cn("group flex items-center gap-6 py-4", className)}
    />
  );
}

export function FileItemTitleAnnotationIcon({
  className,
  tooltip,
  ...props
}: React.ComponentProps<"div"> & {
  tooltip: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          {...props}
          className={cn(
            "rounded-full bg-secondary text-secondary-foreground flex items-center justify-center size-4 [&_svg]:size-2",
            className,
          )}
        />
      </TooltipTrigger>
      <TooltipContent className="bg-secondary border-border text-secondary-foreground">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

export function FileListLabel({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      {...props}
      className={cn(
        "inline-flex [&_svg]:size-4 items-center gap-1 text-xs text-muted-foreground font-medium",
        className,
      )}
    />
  );
}

export function FileList({ className, ...props }: React.ComponentProps<"div">) {
  return <div {...props} className={cn("flex flex-col divide-y", className)} />;
}
